import { getIdToken } from "@/lib/platform/firebase";
import { trackEvent } from "@/lib/analytics/track";

export const CREATORVOICE_API_BASE = "https://creatorvoice-backend-26730182444.us-central1.run.app";

export class CreatorVoiceError extends Error {
  status?: number;
  constructor(message: string, status?: number) {
    super(message);
    this.status = status;
  }
}

async function authedFetch(path: string, options: RequestInit = {}) {
  const token = await getIdToken();
  const res = await fetch(`${CREATORVOICE_API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new CreatorVoiceError(data.detail ? JSON.stringify(data.detail) : `Request failed (${res.status})`, res.status);
  }
  return res.json();
}

// Cloud Run scales to zero between visits, so the first request after idle
// can briefly 503 while an instance cold-starts — retry a couple of times
// before giving up, instead of leaving a dropdown silently empty forever.
async function fetchJsonWithRetry(path: string, retries = 2): Promise<unknown> {
  for (let attempt = 0; ; attempt++) {
    const res = await fetch(`${CREATORVOICE_API_BASE}${path}`);
    if (res.ok) return res.json();
    if (attempt >= retries) throw new CreatorVoiceError(`Request failed (${res.status})`);
    await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
  }
}

export type Voice = {
  id: string;
  label: string;
  language: string;
  language_label: string;
  gender: string;
  category: string;
  is_premium: boolean;
  sample_url: string;
  tier?: "free" | "premium" | "pro";
};

export async function getVoices(): Promise<Voice[]> {
  const data = (await fetchJsonWithRetry("/voices")) as { voices: Voice[] };
  return data.voices;
}

export type Tone = { id: string; label: string; description: string; suggested_speed: number };

export async function getTones(): Promise<Tone[]> {
  const data = (await fetchJsonWithRetry("/tones")) as { tones: Tone[] };
  return data.tones;
}

export async function submitJob(type: string, payload: Record<string, unknown>): Promise<string> {
  trackEvent("ai_tool_click", { tool: type });
  const data = await authedFetch("/jobs", { method: "POST", body: JSON.stringify({ type, payload }) });
  return data.job_id;
}

// Plain HTTP polling fallback for job status — used alongside the Firestore
// realtime listener since some networks silently break Firestore's own
// channel for end users without raising any error.
export async function getJobStatus(jobId: string): Promise<import("@/lib/platform/firebase").JobDoc | null> {
  try {
    return (await authedFetch(`/jobs/${jobId}`)) as import("@/lib/platform/firebase").JobDoc;
  } catch {
    return null;
  }
}

export type ScriptResult = { hook: string; body: string; ending: string; cta: string };

export async function generateScript(topic: string, tone: string, length: string, template: string): Promise<ScriptResult> {
  trackEvent("ai_tool_click", { tool: "script_writer" });
  return authedFetch("/llm/script", { method: "POST", body: JSON.stringify({ topic, tone, length, template }) });
}

export async function getUploadUrl(filename: string, contentType: string): Promise<{ upload_url: string; get_url: string; object_path: string }> {
  return authedFetch("/storage/upload-url", { method: "POST", body: JSON.stringify({ filename, content_type: contentType }) });
}

export async function uploadFileDirect(file: File): Promise<string> {
  const { upload_url, get_url } = await getUploadUrl(file.name, file.type || "application/octet-stream");
  const res = await fetch(upload_url, { method: "PUT", headers: { "Content-Type": file.type || "application/octet-stream" }, body: file });
  if (!res.ok) throw new CreatorVoiceError("Upload to storage failed");
  return get_url;
}
